import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

function mediaImageKey(url) {
  if (!url) return '';
  const cleanUrl = String(url).split('?')[0].split('#')[0];
  const filename = cleanUrl.split('/').pop() || '';
  return filename.replace(/\.(jpe?g|png|webp|gif|avif)$/i, '').toLowerCase();
}

function mediaKeysMatch(a, b) {
  if (!a || !b) return false;
  return a === b || a.startsWith(`${b}_`) || a.startsWith(`${b}-`) || b.startsWith(`${a}_`) || b.startsWith(`${a}-`);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#selectEntryMediaFromUrl();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #selectEntryMediaFromUrl() {
    const productMain = this.closest('main[data-template="product"]');
    if (!productMain || !this.closest('.product-information__media')) return false;
    if (window.__frameluneEntryMediaSelected) return false;

    const params = new URLSearchParams(window.location.search);
    const requestedMediaId = params.get('media') || params.get('featured_media') || params.get('fr_media');
    if (!requestedMediaId) return false;

    const slideshow = this.slideshow;
    if (!slideshow || typeof slideshow.select !== 'function') return false;

    const slides = slideshow.refs?.slides || slideshow.slides || [];
    const targetSlide = slides.find((slide) => {
      if (String(slide.getAttribute('slide-id') || '') === requestedMediaId) return true;

      const mediaElement = slide.querySelector('[data-media-id]');
      const mediaId = String(mediaElement?.dataset.mediaId || '');
      return mediaId === requestedMediaId || mediaId.endsWith(`-${requestedMediaId}`);
    });

    if (!targetSlide) return false;

    const visibleSlides = slideshow.slides || slides;
    const index = visibleSlides.indexOf(targetSlide);
    if (index < 0) return false;

    window.__frameluneEntryMediaSelected = true;
    slideshow.select(index, undefined, { animate: false });
    window.requestAnimationFrame(() => {
      if (typeof slideshow.select === 'function') slideshow.select(index, undefined, { animate: false });
    });
    return true;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.#selectExistingProductMediaForVariant(event)) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Keeps the main product gallery stable during simple same-product color changes.
   * Shopify's default section response moves the selected variant image to the
   * first position; selecting the existing slide avoids a visible double jump.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   * @returns {boolean}
   */
  #selectExistingProductMediaForVariant(event) {
    const productMain = this.closest('main[data-template="product"]');
    if (!productMain || !this.closest('.product-information__media')) return false;
    if (event.detail.data.newProduct) return false;

    const targetMediaId = String(event.detail.resource?.featured_media?.id || '');
    if (!targetMediaId) return false;

    const targetVariantId = String(event.detail.resource?.id || '');
    const colorInputs = Array.from(
      productMain.querySelectorAll('.variant-option--color-inline input[type="radio"][data-preload-image]')
    );
    const selectedColorInput =
      colorInputs.find((input) => String(input.dataset.variantId || '') === targetVariantId) ||
      colorInputs.find((input) => input.checked);
    const selectedImageKey = mediaImageKey(selectedColorInput?.dataset.preloadImage);
    const targetImageKey = mediaImageKey(event.detail.resource?.featured_media?.preview_image?.src);

    // Variant section responses can arrive after the shopper has already chosen
    // another color. Ignore those stale same-product gallery updates so the image
    // does not briefly jump back to the previous color.
    if (selectedImageKey && targetImageKey && !mediaKeysMatch(targetImageKey, selectedImageKey)) {
      return true;
    }

    const slideshow = this.slideshow;
    if (!slideshow || typeof slideshow.select !== 'function') return false;

    const slides = slideshow.refs?.slides || slideshow.slides || [];
    const desiredImageKey = selectedImageKey || targetImageKey;
    const targetSlideBySelectedImage = slides.find((slide) => {
      const slideImage = slide.querySelector('img');
      const slideImageKey = mediaImageKey(slideImage?.currentSrc || slideImage?.getAttribute('src'));
      return desiredImageKey && mediaKeysMatch(slideImageKey, desiredImageKey);
    });

    const targetSlide =
      targetSlideBySelectedImage ||
      slides.find((slide) => {
        const mediaElement = slide.querySelector('[data-media-id]');
        const mediaId = String(mediaElement?.dataset.mediaId || '');
        if (mediaId === targetMediaId || mediaId.endsWith(`-${targetMediaId}`)) return true;

        const slideImage = slide.querySelector('img');
        const slideImageKey = mediaImageKey(slideImage?.currentSrc || slideImage?.getAttribute('src'));
        return targetImageKey && mediaKeysMatch(slideImageKey, targetImageKey);
    });

    if (!targetSlide) return false;

    const visibleSlides = slideshow.slides || slides;
    const index = visibleSlides.indexOf(targetSlide);
    if (index < 0) return false;

    slideshow.select(index, undefined, { animate: false });
    window.requestAnimationFrame(() => {
      if (typeof slideshow.select === 'function') slideshow.select(index, undefined, { animate: false });
    });
    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
